import * as React from "react";
import { getFirstDefined } from "./index";

export const forceRedirect = (to: string) => {
  if (
    typeof window === "undefined" ||
    !window.location ||
    !window.location.pathname ||
    window.location.pathname === to
  ) {
    return;
  }

  window.location.pathname = to;
};

export const getDocument = (ref?: React.RefObject<any> | HTMLElement) => {
  const fallback = document.ownerDocument || document;

  if (!ref) {
    return fallback;
  }

  if (ref instanceof Element) {
    return ref.ownerDocument || fallback;
  }

  if (ref.current && ref.current instanceof Element) {
    return ref.current.ownerDocument || fallback;
  }

  return fallback;
};

type CSSKey = keyof React.CSSProperties;

export const createCSSGetterSetter = (el: HTMLElement) => (prop: CSSKey, val?: string | null) => {
  if (typeof val === "undefined") {
    // @ts-ignore: this is okay
    return el.style[prop];
  }
  /* eslint-disable no-param-reassign */
  // @ts-ignore
  el.style[prop] = val;
  /* eslint-enable no-param-reassign */

  return undefined;
};

export const createScrollLock = () => {
  // We're just going to keep this as the prevScroll so we have it. We use this to restore the
  // scroll position when we lock the scroll
  const prevScroll = { x: 0, y: 0 };
  const prevStyle: { [key: string]: string | null } = { height: "", overflow: "" };

  // We treat the empty object as a document for when we're in a gatsby ssr context
  const DOC = typeof document !== "undefined" ? getDocument() : ({} as Document);
  const doc = DOC ? DOC.documentElement : document.documentElement;

  const bodyCSS = createCSSGetterSetter(doc);

  const getScrollPosition = () => {
    if (typeof window === "undefined") {
      return prevScroll;
    }

    const x =
      getFirstDefined(window.scrollX, window.pageXOffset, doc.scrollLeft) - (doc.clientLeft || 0);
    const y =
      getFirstDefined(window.scrollY, window.pageYOffset, doc.scrollTop) - (doc.clientTop || 0);

    return { x, y };
  };

  // we need a better way to restore the window from before multiple locks...
  const unlockScroll = () => {
    // this works for just one lock
    // Object.keys(prevStyle).forEach(key => {
    // bodyCSS(key as CSSKey, prevStyle[key]);
    // });

    bodyCSS("height", null);
    bodyCSS("overflow", null);

    // eslint-disable-next-line no-unused-expressions
    typeof window !== "undefined" && window.scrollTo(prevScroll.x, prevScroll.y);
  };

  /**
   * Locks the scroll
   *
   * @returns an unlock scroll function
   */
  const lockScroll = () => {
    const { x, y } = getScrollPosition();

    if (prevScroll.x !== x) {
      prevScroll.x = x;
    }

    if (prevScroll.y !== y) {
      prevScroll.y = y;
    }

    if (prevStyle.height !== bodyCSS("height")) {
      prevStyle.height = bodyCSS("height");
    }

    if (prevStyle.overflow !== bodyCSS("overflow")) {
      prevStyle.overflow = bodyCSS("overflow");
    }

    bodyCSS("height", "100vh");
    bodyCSS("overflow", "hidden");

    return unlockScroll;
  };

  return { position: getScrollPosition, lock: lockScroll, unlock: unlockScroll };
};

export const SCROLL = createScrollLock();

export const getDocumentReferrer = () => {
  if (typeof document === "undefined") {
    return "";
  }
  return typeof document.referrer === "string" ? document.referrer : "";
};
